import { faCheckCircle, faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "@mui/material";

import { OrderDeliveryAddress } from "../../../../core/models/entities/Order";
import { ColumnSettings } from "../../../components/GenericTable/SubComponents/ListFrame";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import FunctionalList from "../../../components/GenericTable/FunctionalList";
import { useWhiteLabelContext } from "../../../../WhiteLabelContext";
import { faX } from "@fortawesome/free-solid-svg-icons";

interface DeliveryAddressDialogProps {
  addresses: OrderDeliveryAddress[];
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  onSelectAddress: (address: OrderDeliveryAddress) => void;
  onToggleAddress: (address: OrderDeliveryAddress) => void;
}

export default function DeliveryAddressDialog({
  addresses,
  isOpen,
  setOpen,
  onSelectAddress,
  onToggleAddress,
}: DeliveryAddressDialogProps) {
  const { whiteLabel } = useWhiteLabelContext();

  return (
    <Modal
      aria-labelledby="delivery-address-modal-title"
      aria-describedby="delivery-address-modal-description"
      open={isOpen}
      onClose={() => setOpen(false)}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className={
          "rounded bg-white min-w-[800px] max-w-[90vw] max-h-[90vh] overflow-hidden relative flex flex-col"
        }
        onClick={(e) => e.stopPropagation()}
        style={
          {
            "--primary": whiteLabel.colorHex,
            "--primary-bg": `${whiteLabel.colorHex}10`,
          } as React.CSSProperties
        }
      >
        <div className={"absolute z-10 right-0 top-0 p-2"}>
          <button
            onClick={() => setOpen(false)}
            className={
              "!h-8 !w-8 z-40 hover:bg-gray-100 rounded flex items-center justify-center"
            }
          >
            <FontAwesomeIcon
              className={"text-sm"}
              style={{ fontSize: 10, lineHeight: 10 }}
              icon={faX}
            />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" style={{ minHeight: "auto" }}>
          <div
            style={{ minHeight: "auto" }}
            className="[&_.min-h-\[calc\(100vh-17rem\)\]]:min-h-0 [&_p.uppercase.font-bold]:normal-case"
          >
            <FunctionalList<OrderDeliveryAddress>
              tableTitle="Choose from previous addresses"
              data={addresses}
              getKey={(data) => data.id}
              columns={[
                {
                  value: "name",
                  displayValue: "Company",
                  weight: 3,
                },
                {
                  value: "contactPerson",
                  displayValue: "Contact",
                  weight: 3,
                },
                {
                  value: "address",
                  displayValue: "Address",
                  weight: 3,
                },
                {
                  value: "zipCity",
                  displayValue: "Zip Code / City",
                  weight: 2,
                },
                {
                  value: "default",
                  displayValue: "Set as Default",
                  weight: 1,
                },
              ]}
              isLoading={false}
              lastPage={1}
              onRowClick={(address) => {
                onSelectAddress(address);
                setOpen(false);
              }}
              renderRow={(
                columns: ColumnSettings,
                address: OrderDeliveryAddress
              ) => (
                <StrucRow className={"py-4"}>
                  <StrucCol size={columns.name.weight}>{address.name}</StrucCol>
                  <StrucCol size={columns.contactPerson.weight}>
                    {address.contact}
                  </StrucCol>
                  <StrucCol size={columns.address.weight}>
                    {address.address}
                  </StrucCol>
                  <StrucCol size={columns.zipCity.weight}>
                    {address.zip} {address.city}
                  </StrucCol>
                  <StrucCol size={columns.default.weight}>
                    <div
                      className="ml-[-10px] flex flex-col items-center justify-center w-full cursor-pointer"
                      onClickCapture={(event) => {
                        onToggleAddress(address);
                        event.stopPropagation();
                      }}
                    >
                      {address.isDefault ? (
                        <FontAwesomeIcon
                          size="2xl"
                          color="#7BBB58"
                          icon={faCheckCircle}
                        />
                      ) : (
                        <FontAwesomeIcon
                          size="2xl"
                          color="#EEEEEE"
                          icon={faCircle}
                        />
                      )}
                    </div>
                  </StrucCol>
                </StrucRow>
              )}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
